import {BulletedList} from 'shells/bulleted-list/bulleted-list';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {WarningWithPopover} from 'shells/table/warning-with-popover';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {Body} from 'shells/typography/body';

import type {GetSubmission_submission_notificationLogs as SubmissionNotificationLog} from 'nutshell-graphql-types';

const COULD_NOT_SEND_MESSAGE = 'This notification could not be sent, please contact support.';

type Props = {
    notificationLogs: SubmissionNotificationLog[];
    isLoading: boolean;
};

export function SubmissionNotificationLogs(props: Props) {
    if (props.isLoading) {
        return <LoadingPage />;
    }

    if (props.notificationLogs.length === 0) {
        return <Body color='light'>No notifications were sent for this submission.</Body>;
    }

    return (
        <div className='mt-8'>
            <BulletedList
                bullets={props.notificationLogs.map((notificationLog) => {
                    const recipientText = notificationLog.recipient
                        ? ` to ${notificationLog.recipient.name}`
                        : '';

                    return (
                        <div key={notificationLog.id} className='flex align-center gap-4'>
                            {notificationLog.didNotSend && (
                                <WarningWithPopover
                                    popoverContent={
                                        <div className='pad-4'>
                                            <Body size='small'>{COULD_NOT_SEND_MESSAGE}</Body>
                                        </div>
                                    }
                                />
                            )}
                            <Body>
                                {getNotificationTypeLabel(notificationLog.sendType)}
                                {notificationLog.didNotSend ? ' failed to send' : ' sent'}
                                {recipientText}
                                {' on '}
                                <FormattedTimestamp
                                    datetime={Number(notificationLog.createdTime)}
                                    format={FormattedTimestamp.formats.shortDayMonthWithAtTime}
                                    isInline={true}
                                />
                            </Body>
                        </div>
                    );
                })}
                greyBullets={true}
            />
        </div>
    );
}

function getNotificationTypeLabel(sendType: SubmissionNotificationLog['sendType']) {
    switch (sendType) {
        case 'EMAIL':
            return 'Email';
        case 'SMS':
            return 'SMS';
        case 'PUSH':
            return 'Mobile push';
        case 'IN_APP':
            return 'In-app notification';
        case 'WEBHOOK':
            return 'Webhook';
        default:
            return 'Notification';
    }
}
